import { ActionReducerMapBuilder, createSlice, PayloadAction } from "@reduxjs/toolkit";
import RequestStatus from "../RequestStatus";
import {
  ClientLens,
  CollectUserView,
  FirmWithLenses,
  PendingCollectUserView,
  PendingUserView,
  SimpleLensView,
  UserInfo,
  UserType,
} from "generated/graphql";
import {
  fetchFirms,
  fetchLenses,
  fetchClientLens,
  fetchUsers,
  inviteUser,
  setCollectUserPermissions,
  updateCollectUserFirms,
  updateCollectUserLensPermission,
  updateCollectUserRole,
  updateLensPermission,
  updateUserDeactivated,
  deleteFirm,
  addLensesToFirm,
  createFirm,
  updateFirm,
  deleteLensesFromFirm,
  inviteCollectUser,
  resendCollectInvite,
} from "./client.action";
import { USER_LOGOUT } from "redux/system.action";
import { getAppHome } from "../session";

export interface ClientState {
  usersStatus: RequestStatus;
  users: UserInfo[];
  pendingUsers: PendingUserView[];
  pendingCollectUsers: PendingCollectUserView[];
  collectUsersStatus: RequestStatus;
  collectUsers: CollectUserView[];
  firmsStatus: RequestStatus;
  firms: FirmWithLenses[];
  activeFirmId: string;
  lensesStatus: RequestStatus;
  lenses: SimpleLensView[];
  clientLenses: {
    [key: number]: ClientLens;
  };
}
const initialState: ClientState = {
  usersStatus: RequestStatus.IDLE,
  users: [],
  pendingUsers: [],
  pendingCollectUsers: [],
  collectUsersStatus: RequestStatus.IDLE,
  collectUsers: [],
  firmsStatus: RequestStatus.IDLE,
  firms: [],
  activeFirmId: "",
  lensesStatus: RequestStatus.IDLE,
  lenses: [],
  clientLenses: {},
};

// Client Reducers
const clientExtraReducers = (builder: ActionReducerMapBuilder<ClientState>): void => {
  builder.addCase(USER_LOGOUT, () => {
    return { ...initialState };
  });
};
// Users Reducers
const usersExtraReducers = (builder: ActionReducerMapBuilder<ClientState>): void => {
  builder
    .addCase(fetchUsers.pending, (state) => {
      state.usersStatus = RequestStatus.PENDING;
    })
    .addCase(fetchUsers.fulfilled, (state, action) => {
      state.usersStatus = RequestStatus.SUCCEEDED;
      state.users = action.payload.users.users;
      state.pendingUsers = action.payload.users.pendingUsers;
      state.pendingCollectUsers = action.payload.users.pendingCollectUsers;
    })
    .addCase(fetchUsers.rejected, (state) => {
      state.usersStatus = RequestStatus.FAILED;
    })
    .addCase(updateUserDeactivated.fulfilled, (state, action) => {
      const { userId } = action.payload;
      const userToUpdate = state.users.find((user) => user.id === userId);
      if (userToUpdate) {
        userToUpdate.deactivated = action.payload.deactivated;
      }
    });
  builder
    .addCase(getAppHome.pending, (state) => {
      state.usersStatus = RequestStatus.PENDING;
      state.lensesStatus = RequestStatus.PENDING;
      state.collectUsersStatus = RequestStatus.PENDING;
    })
    .addCase(getAppHome.fulfilled, (state, action) => {
      state.usersStatus = RequestStatus.SUCCEEDED;
      state.users = action.payload.users.users;
      state.pendingUsers = action.payload.users.pendingUsers;
      state.pendingCollectUsers = action.payload.users.pendingCollectUsers;
      state.collectUsersStatus = RequestStatus.SUCCEEDED;
      state.collectUsers = action.payload.users.collectUsers;
      state.lenses = action.payload.lensesNew;
      state.lensesStatus = RequestStatus.SUCCEEDED;
    });

  builder.addCase(inviteUser.fulfilled, (state, action) => {
    const pending = action.payload;

    state.pendingUsers.push({
      email: pending.email,
      clientId: -1,
      permissions: [],
      userType: pending.admin ? UserType.Admin : UserType.Reviewer,
      invitedAt: new Date().toISOString(),
      isExpired: false,
      firstName: pending.firstName,
      lastName: pending.lastName,
    });
  });
  builder.addCase(inviteCollectUser.fulfilled, (state, action) => {
    state.pendingCollectUsers.push(action.payload.users.inviteCollectUser);
  });
  builder.addCase(resendCollectInvite.fulfilled, (state, action) => {
    const email = action.payload.email;
    const user = state.pendingCollectUsers.find((u) => u.email === email);
    if (user) {
      user.lastInvitedAt = new Date().toISOString();
      user.isExpired = false;
    }
  });
  builder.addCase(updateLensPermission.fulfilled, (state, action) => {
    const { userId, lens, value } = action.payload;
    const userToUpdate = state.users.find((user) => user.id === userId);
    if (userToUpdate) {
      const newLenses = value ? [...userToUpdate.lenses, lens] : userToUpdate.lenses.filter((l) => l.id !== lens.id);
      userToUpdate.lenses = newLenses;
    }
  });
};
// Collect Users Reducers
const collectUsersExtraReducers = (builder: ActionReducerMapBuilder<ClientState>): void => {
  builder
    .addCase(updateCollectUserLensPermission.fulfilled, (state, action) => {
      const foundUser = state.collectUsers.find((u) => u.id === action.payload.collectUserId);
      const addedLenses = action.payload.lensPermissions.filter((lp) => lp.status).map((al) => al.lensId);
      const removedLenses = new Set(action.payload.lensPermissions.filter((lp) => !lp.status).map((lp) => lp.lensId));

      if (foundUser) {
        foundUser.lensIds.push(...addedLenses);
        foundUser.lensIds = foundUser.lensIds.filter((lensId) => !removedLenses.has(lensId));
      }
    })
    .addCase(updateCollectUserFirms.fulfilled, (state, { payload }) => {
      const foundUser = state.collectUsers.find((u) => u.id === payload.collectUserId);

      if (foundUser) {
        foundUser.firms = payload.firms;
      }
    })
    .addCase(setCollectUserPermissions.fulfilled, (state, action) => {
      const foundUser = state.collectUsers.find((u) => u.id === action.payload.collectUserId);
      if (foundUser) {
        foundUser.permissions = action.payload.permissions.filter((p) => p.status).map((p) => p.permission);
      }
    })
    .addCase(updateCollectUserRole.fulfilled, (state, action) => {
      const foundUser = state.collectUsers.find((u) => u.id === action.payload.collectUserId);

      if (foundUser) {
        foundUser.role = action.payload.role;
      }
    });
};
// Firms Reducers
const firmsReducers = {
  setActiveFirmId(state, action: PayloadAction<string>) {
    state.activeFirmId = action.payload;
  },
};
const firmsExtraReducers = (builder: ActionReducerMapBuilder<ClientState>): void => {
  builder
    .addCase(fetchFirms.pending, (state) => {
      state.firmsStatus = RequestStatus.PENDING;
    })
    .addCase(fetchFirms.fulfilled, (state, action) => {
      state.firmsStatus = RequestStatus.SUCCEEDED;
      state.firms = action.payload
        .slice()
        .sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
    })
    .addCase(fetchFirms.rejected, (state) => {
      state.firmsStatus = RequestStatus.FAILED;
    })
    .addCase(deleteFirm.fulfilled, (state, { payload }) => {
      state.firms = state.firms.filter((firm) => firm.id !== payload);
    })
    .addCase(
      deleteLensesFromFirm.fulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: { firmId },
          },
        }
      ) => {
        const foundFirm = state.firms.find((firm) => firm.id === firmId);
        if (foundFirm !== undefined) {
          foundFirm.lensIds = foundFirm.lensIds.filter((lensId) => !payload.firm.deleteLensesFromFirm.includes(lensId));
        }
      }
    )
    .addCase(
      addLensesToFirm.fulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: { firmId },
          },
        }
      ) => {
        const foundFirm = state.firms.find((firm) => firm.id === firmId);
        if (foundFirm !== undefined) {
          foundFirm.lensIds = foundFirm.lensIds.concat(payload.firm.addLensesToFirm);
        }
      }
    )
    .addCase(createFirm.fulfilled, (state, { payload }) => {
      state.firms.push(payload.firm.upsertFirm);
    })
    .addCase(updateFirm.fulfilled, (state, { payload }) => {
      const foundFirmIndex = state.firms.findIndex((firm) => firm.id === payload.firm.upsertFirm.id);
      if (foundFirmIndex !== -1) {
        state.firms[foundFirmIndex] = payload.firm.upsertFirm;
      }
    });
};
// Lenses Reducers
const lensesExtraReducers = (builder: ActionReducerMapBuilder<ClientState>): void => {
  builder
    .addCase(fetchLenses.pending, (state) => {
      state.lensesStatus = RequestStatus.PENDING;
    })
    .addCase(fetchLenses.fulfilled, (state, action) => {
      state.lensesStatus = RequestStatus.SUCCEEDED;
      state.lenses = action.payload;
    })
    .addCase(fetchLenses.rejected, (state) => {
      state.lensesStatus = RequestStatus.FAILED;
    })
    .addCase(fetchClientLens.fulfilled, (state, action) => {
      state.clientLenses[action.payload.lens.id] = action.payload;
    });
};

const clientSlice = createSlice({
  name: "client",
  initialState,
  reducers: {
    ...firmsReducers,
  },
  extraReducers: (builder) => {
    clientExtraReducers(builder);
    usersExtraReducers(builder);
    collectUsersExtraReducers(builder);
    firmsExtraReducers(builder);
    lensesExtraReducers(builder);
  },
});

export const { setActiveFirmId } = clientSlice.actions;

export default clientSlice;
